<script lang="ts">
import { defineComponent, defineAsyncComponent } from 'vue'
import { User } from '../../models'
import { useEmitter } from '../composition/useEmitter'
import { USER_ROLE, USER_TYPE } from '../../models/user'
import { isCurrentlySnoozed, setSnooze } from '../../util/nudge_utils'
import { loadRemote } from '@module-federation/runtime'
import moment from 'moment'
import { OnboardingEvents } from '@/events'
import { loadFreshWorksWidgets } from '@/util/freshchat'

export default defineComponent({
  name: 'CommonConditionalComponents',
  components: {
    LaunchpadHighlyDrawer: defineAsyncComponent(() => import('@/pmd/pages/launchpad/LaunchpadHighlyDrawer.vue')),
    NudgeComponent: defineAsyncComponent(() => import('@/pmd/pages/nudges/NudgeComponent.vue')),
    LaunchpadInactiveModal: defineAsyncComponent(() => import('@/pmd/components/launchpad/LaunchpadInactiveModal.vue')),
  },
  data() {
    return {
      isLaunchpadEnabled: false,
      isLoggedOut: false,
      showLaunchpadInactiveModal: false,
      emitter: useEmitter(),
    }
  },
  mounted() {
    this.emitter.off('common-conditional-components-logout', this.handleLogout)
    this.emitter.on('common-conditional-components-logout', this.handleLogout)

    this.emitter.on(OnboardingEvents.INIT_FRESHCHAT_IMPLEMENTATION_BANNER_ONBOARDING, async () => {
      await loadFreshWorksWidgets({
        widgetType: 'implementation',
        ooh_source: 'kickoff-page',
      })
    })
  },
  watch: {
    '$route.params.location_id': async function (id) {
      if (id) {
        await this.isLocationLaunchpadAssigned()
      }
    },
    companyId: async function (id: string) {
      if (id) {
        await this.checkLaunchpadEnabled()
      }
    },
    '$route.path': async function (newPath: string) {
      // Handle inactive nudge logic on route changes
      this.handleInactiveNudgeLogic(newPath)
    },
  },
  methods: {
    async checkLaunchpadEnabled() {
      try {
        if (this.$route?.params?.location_id) {
          await this.isLocationLaunchpadAssigned()
        } else {
          await this.isAgencyLaunchpadAssigned()
        }
      } catch (err) {
        //
      }
    },
    async isAgencyLaunchpadAssigned() {
      try {
        const user = await this.$store.dispatch('user/get')
        // if user is agency user and role is user, `then hide the agency launchpad
        if (user.type === User.TYPE_AGENCY && user.role === User.ROLE_USER) {
          this.isLaunchpadEnabled = false
          return
        }

        const company = this.$store.state.company.company
        const dateAdded = company.date_added || company.dateAdded
        if (dateAdded && moment(dateAdded).isAfter('2025-10-14')) {
          this.isLaunchpadEnabled = true
        } else {
          const isAgencyLaunchpadAssigned = await this.$store.dispatch('launchpad/isAgencyLaunchpadAssigned')
          this.isLaunchpadEnabled = isAgencyLaunchpadAssigned
        }
      } catch (err) {
        this.isLaunchpadEnabled = false
      }
    },
    async isLocationLaunchpadAssigned() {
      try {
        const locationId = this.$store.state.locations.currentLocation?.id || this.$route.params.location_id
        if (locationId) {
          this.isLaunchpadEnabled = await this.$store.dispatch('launchpad/redirectToLaunchpad', locationId)
        }
      } catch (err) {
        //
      }
    },
    handleLogout() {
      this.isLoggedOut = true
    },
    async handleInactiveNudgeLogic(newPath: string) {
      try {
        this.showLaunchpadInactiveModal = false
        if (!this.isInactiveNudgeAllowed()) {
          return
        }

        const { initiateValuePropInactiveNudge } = await this.getNudgeUtils()
        const initValuePropNudge = await initiateValuePropInactiveNudge(this.$store, this.emitter)
        const companyId = this.$store?.state?.company?.company?.id

        if (initValuePropNudge) {
          // Set snooze for 24 hours when value prop nudge is triggered
          setSnooze('launchpad-inactive-value-prop-nudge-snooze', companyId)
        } else {
          // Check if we're currently snoozed
          const isSnoozed = isCurrentlySnoozed('launchpad-inactive-value-prop-nudge-snooze', companyId)

          // Only show modal if not snoozed and not on launchpad route
          if (!isSnoozed && !newPath?.includes('launchpad')) {
            this.showLaunchpadInactiveModal = true
          }
        }
      } catch (error) {
        console.error('Error in handleInactiveNudgeLogic:', error)
      }
    },
    isInactiveNudgeAllowed() {
      try {
        // Before loading this nudge check for basic conditions
        if (this.$store?.state?.company?.company?.hide_launchpad) {
          return false
        }

        // only agency admins
        if (
          this.$store?.state?.user?.user?.role !== USER_ROLE.ADMIN ||
          this.$store?.state?.user?.user?.type !== USER_TYPE.AGENCY
        ) {
          return false
        }

        // agency is in trial
        if (!this.$store?.state?.company?.company?.status?.includes('active_trial')) {
          return false
        }

        // if in location then it should be the default location
        if (
          this.$route.params.location_id &&
          this.$route.params.location_id !== this.$store?.state?.onboarding?.onboardingInfo?.defaultLocationId
        ) {
          return false
        }

        // for a/b
        if (!this.$store?.state?.onboarding?.onboardingInfo?.showInactiveTrialNudge) {
          return false
        }

        const companyId = this.$store?.state?.company?.company?.id

        // Check if we're already in a snooze period
        if (isCurrentlySnoozed('launchpad-trial-end-popup-snooze', companyId)) {
          return false
        }

        // If billing trial popup is active, snooze our nudge for 24 hours
        const trialEndPopupClosed = localStorage.getItem(`trial-end-popup-closed-${companyId}`)
        if (trialEndPopupClosed === 'false') {
          setSnooze('launchpad-trial-end-popup-snooze', companyId)
          return false
        }
        return true
      } catch (error) {
        console.error('Error in isInactiveNudgeAllowed:', error)
        return false
      }
    },
    async getNudgeUtils() {
      const { initiateValuePropInactiveNudge } = (await loadRemote('launchpadApp/utils')) as any
      return { initiateValuePropInactiveNudge }
    },
  },
  computed: {
    launchpadEnabled() {
      return this.isLaunchpadEnabled
    },
    companyId() {
      return this.$store.state?.company?.company?.id
    },
  },
  beforeUnmount() {
    this.emitter.off(OnboardingEvents.INIT_FRESHCHAT_IMPLEMENTATION_BANNER_ONBOARDING)
  },
})
</script>
<template>
  <div v-if="!isLoggedOut">
    <LaunchpadHighlyDrawer v-if="launchpadEnabled" />
    <NudgeComponent v-if="companyId" :company-id="companyId" />
    <LaunchpadInactiveModal v-if="showLaunchpadInactiveModal" />
  </div>
</template>
